import { useState } from 'react';
import { useNavigate } from 'react-router';
import { GlobeIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { DataRow, DataTable, RowActions, TableCell, TableHead } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { EmptyState } from '../shell/EmptyState.js';
import { IconButton } from '../components/IconButton.js';
import { formatCount } from '../lib/format.js';
import { useAuth } from '../auth/useAuth.js';
import { GroupForm } from './GroupForm.js';
import {
  useCreateGroup,
  useDeleteGroup,
  useGroups,
  useUpdateGroup,
  type GroupSummary,
} from './queries.js';

/** Group name, URL count, row actions. */
const COLUMNS = ['70%', '15%', '15%'] as const;

/** Cascade counts read as prose in the confirmation, so they are pluralised. */
const plural = (count: number, noun: string): string =>
  `${formatCount(count)} ${noun}${count === 1 ? '' : 's'}`;

export function GroupsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const groups = useGroups();
  const create = useCreateGroup();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<GroupSummary | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GroupSummary | null>(null);
  /* Both hooks are id-parameterised. The id comes from the row that opened the
     dialog, and 0 stands in while nothing is open — no request is made then. */
  const update = useUpdateGroup(editing?.id ?? 0);
  const remove = useDeleteGroup();
  const canEdit = user?.role === 'admin';

  return (
    <section aria-labelledby="groups-heading" className="flex flex-col gap-(--space-5)">
      <header className="flex flex-wrap items-end justify-between gap-(--space-4)">
        <div>
          <p className="text-[length:var(--text-micro)] uppercase tracking-[0.18em] text-ink-subtle">
            Clients
          </p>
          <h1 id="groups-heading" className="mt-(--space-1) text-[length:var(--text-display)]">
            Groups
          </h1>
        </div>
        {canEdit && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            New group
          </Button>
        )}
      </header>

      {groups.isError && (
        <Callout tone="danger">
          {groups.error instanceof Error ? groups.error.message : 'Could not load groups'}
        </Callout>
      )}

      {groups.isLoading && (
        <p role="status" className="text-ink-subtle">
          Loading groups…
        </p>
      )}

      {groups.data?.groups.length === 0 && (
        <EmptyState
          title="No clients yet"
          description="A group holds one client's domains and campaign paths. Create your first group, add the domain they will point at us, then configure the paths."
          action={
            canEdit ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Create your first group
              </Button>
            ) : undefined
          }
        />
      )}

      {groups.data !== undefined && groups.data.groups.length > 0 && (
        <DataTable
          /* The stat and the actions get a fixed slice at the right, so the
             number sits beside the buttons it belongs with rather than
             floating wherever the longest client name leaves it. */
          columns={COLUMNS}
          caption="Configured client groups"
          head={
            <>
              <TableHead scope="col">Group</TableHead>
              {/* One stat, and it is the one the console is organised around.
                  How many domains a group needs to serve those URLs is a
                  detail of the configuration, not a measure of the client. */}
              <TableHead scope="col" className="text-right">
                URLs
              </TableHead>
              <TableHead scope="col" className="text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            </>
          }
        >
          {groups.data.groups.map((group) => (
            <DataRow
              key={group.id}
              /* The group's URLs, not its domains. A group is a client, and
                 what an operator wants from a client is the list of links
                 configured for them — which is also the screen every other
                 route into a group now lands on. */
              onActivate={() => void navigate(`/urls?group=${group.id}`)}
              activateLabel={`Open URLs for ${group.name}`}
            >
              <TableCell className="py-(--space-3)">
                {/* 700 for the name, 500 for the line under it. At this
                    density near-black text on near-white is uniformly grey to
                    scan; weight is what separates the thing from its
                    description. */}
                <span className="font-bold text-ink">{group.name}</span>
                {group.notes !== '' && (
                  <p className="mt-(--space-1) max-w-[60ch] text-[length:var(--text-small)] font-medium text-ink-muted">
                    {group.notes}
                  </p>
                )}
              </TableCell>
              <TableCell className="py-(--space-3) text-right tabular-nums">
                {formatCount(group.entryCount)}
              </TableCell>
              <TableCell className="py-(--space-3)">
                {canEdit && (
                  <RowActions>
                    {/* Domains live here and nowhere else in the primary
                        path: they are a hostname-level detail — DNS, a
                        per-domain fallback, taking a client offline — that
                        matters a few times a year, so it gets one deliberate
                        way in rather than a screen of its own in the nav. */}
                    <IconButton
                      label={`Domains in ${group.name}`}
                      onClick={() => void navigate(`/groups/${group.id}`)}
                    >
                      <GlobeIcon aria-hidden="true" />
                    </IconButton>
                    <IconButton label={`Edit ${group.name}`} onClick={() => setEditing(group)}>
                      <PencilIcon aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={`Delete ${group.name}`}
                      side="left"
                      className="text-danger hover:bg-(--color-danger-quiet) hover:text-danger"
                      onClick={() => setPendingDelete(group)}
                    >
                      <Trash2Icon aria-hidden="true" />
                    </IconButton>
                  </RowActions>
                )}
              </TableCell>
            </DataRow>
          ))}
        </DataTable>
      )}

      <Dialog open={creating} title="New group" onClose={() => setCreating(false)}>
        <GroupForm
          submitLabel="Create group"
          onCancel={() => setCreating(false)}
          onSubmit={async (input) => {
            await create.mutateAsync(input);
            setCreating(false);
          }}
        />
      </Dialog>

      <Dialog open={editing !== null} title="Edit group" onClose={() => setEditing(null)}>
        {editing !== null && (
          <GroupForm
            initial={{ name: editing.name, notes: editing.notes }}
            submitLabel="Save group"
            onCancel={() => setEditing(null)}
            onSubmit={async (input) => {
              await update.mutateAsync(input);
              setEditing(null);
            }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete group"
        body={`Delete ${pendingDelete?.name ?? ''}? ${plural(pendingDelete?.domainCount ?? 0, 'domain')} and ${plural(pendingDelete?.entryCount ?? 0, 'configured path')} go with it — traffic arriving on those hostnames falls through to the global fallback. This cannot be undone.`}
        confirmLabel="Delete group"
        busy={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete === null) return;
          await remove.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </section>
  );
}
